import { useTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useLayoutEffect, useRef, useState } from "react";
import {
  CanvasTexture,
  Color,
  MathUtils,
  type Mesh,
  type MeshStandardMaterial,
} from "three";
import { useProductViewerAssets } from "./productViewerAssets";
import {
  readProductFootprintRadius,
  STUDIO_FLOOR_MAX_RADIUS,
  STUDIO_FLOOR_SOLID,
  STUDIO_FLOOR_UNMEASURED_RADIUS,
  studioFloorRadiusForFootprint,
} from "./studioFloorExtent";
import { useOwnedStudioFloorTextures } from "./studioFloorTextures";

/*
 * "Beige Wall 001", Poly Haven (CC0). The 1k JPEGs as published, 267 KB for
 * the three, so no re-encode — the lightest set tried here, and well under
 * the 508 KB the painted concrete before it needed even after one. The 3 m
 * square tile gives 3.4 px/cm at 1k against the case's roughly 11 px/cm on
 * screen from the default orbit.
 *
 * A wall used as a floor, and a smooth one: its diffuse spans only 116-168
 * in sRGB and compresses to 33 KB, so it carries far less incident than the
 * concretes did. That is the look — flat plaster rather than aggregate — but
 * it means the relief is almost entirely the normal map's. The photograph's
 * beige is corrected out by {@link FLOOR_ALBEDO_LIFT}; what is wanted from
 * this asset is its evenness, not its colour.
 * URLs come from the provider's stage asset manifest, not a second file set.
 * @see https://polyhaven.com/a/beige_wall_001
 */

/**
 * Multiplier on the diffuse map. The photograph averages 158/140/120 in sRGB,
 * a linear albedo of 0.340/0.262/0.188 — already bright, where every concrete
 * tried here was dark enough to need lifting two to four times, but strongly
 * beige: half as much blue as red.
 *
 * Per channel, so that each lands on the same 0.29 linear and the cast is
 * gone — the surface renders as a neutral 147/147/147 grey, keeping the
 * plaster's relief and its wear while dropping its colour. Brightness is
 * untouched by this: the weighted luminance was already 0.29 under the flat
 * 1.06 this replaces, so only the chroma moved.
 *
 * Half of this correction, 0.956/1.083/1.302, leaves a trace of the warmth at
 * the same luminance, if a dead neutral ever reads too cold against the case.
 */
const FLOOR_ALBEDO_LIFT = new Color(0.852, 1.106, 1.543);

/**
 * How much of the baked occlusion map to apply. This one averages 0.964 and
 * bottoms out at 0.79 — smooth plaster, so it darkens only the few dents and
 * the shallow trowel marks — and at full strength that is what keeps them
 * from looking painted on. An early aggregate averaged 0.56 and had to be
 * held back to 0.35 or the whole patch read as dirt.
 */
const FLOOR_AO_INTENSITY = 1;

/**
 * Edge length of the alpha map. The fade is a smooth radial ramp, and this
 * is far more than it needs.
 */
const FADE_MAP_SIZE = 256;

/**
 * A radial alpha ramp: opaque out to `solid` of the radius, then falling to
 * zero at the rim. Smoothstep, so that neither end shows a crease.
 *
 * Written as grey, not as alpha: three samples an `alphaMap`'s green channel.
 */
function createFadeMap(solid: number): CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = FADE_MAP_SIZE;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("2D canvas context unavailable");
  const image = context.createImageData(FADE_MAP_SIZE, FADE_MAP_SIZE);
  const half = FADE_MAP_SIZE / 2;
  for (let y = 0; y < FADE_MAP_SIZE; y++) {
    for (let x = 0; x < FADE_MAP_SIZE; x++) {
      const dx = (x + 0.5 - half) / half;
      const dy = (y + 0.5 - half) / half;
      const r = Math.hypot(dx, dy);
      // `MathUtils.smoothstep` is the same Hermite ramp, with the two clamps
      // as early returns — so `solid === 1` is 0/1 rather than a divide by
      // zero, and the texels outside the ramp cost no division.
      const alpha = 1 - MathUtils.smoothstep(r, solid, 1);
      const i = (y * FADE_MAP_SIZE + x) * 4;
      image.data[i] =
        image.data[i + 1] =
        image.data[i + 2] =
          Math.round(alpha * 255);
      image.data[i + 3] = 255;
    }
  }
  context.putImageData(image, 0, 0);
  return new CanvasTexture(canvas);
}

interface StudioFloorProps {
  /** Height of the patch, in metres. */
  y: number;
  /**
   * Fixed radius of the patch, in metres, capped at
   * {@link STUDIO_FLOOR_MAX_RADIUS}. Omit to follow the product: the patch
   * then tracks whatever the floor aligner last measured, so it grows and
   * shrinks with the case as it is being resized.
   */
  radius?: number;
  /** Fraction of the radius that is fully opaque before the fade begins. */
  solid?: number;
}

/**
 * A pinned radius, held to the same ceiling the tracked radius respects and to
 * a finite, non-negative value.
 *
 * The finiteness check is not ceremony. `Math.min(NaN, x)` is NaN, and NaN
 * never equals itself, so a NaN radius defeats both equality guards in the
 * frame callback below: the mesh scale and all three texture matrices would be
 * rewritten every frame, forever, with the disc gone from the picture. A
 * negative one would mirror the disc and invert its UVs, and zero would leave
 * no disc at all. The tracked branch rejects all three in
 * {@link studioFloorRadiusForFootprint}, and this one answers them the same
 * way — including sending its own fallback through the ceiling, so the two
 * cannot drift apart if the constants are ever retuned.
 */
function clampPinnedRadius(radius: number): number {
  if (!Number.isFinite(radius) || radius <= 0) {
    return Math.min(STUDIO_FLOOR_MAX_RADIUS, STUDIO_FLOOR_UNMEASURED_RADIUS);
  }
  return Math.min(radius, STUDIO_FLOOR_MAX_RADIUS);
}

/**
 * Sizes the patch every frame from the product's footprint.
 *
 * The radius is written straight to the mesh and the texture matrices rather
 * than held in React state, so a resize is followed frame by frame without a
 * re-render per frame. Both are cheap on purpose: the geometry is a unit
 * circle that is only ever scaled — never rebuilt, which a `circleGeometry`
 * taking the radius as an argument would be — and `setRadius` moves the
 * texture repeat, which is a uniform and leaves the image alone.
 *
 * A fixed `radius` skips all of this and pins the patch, up to the same
 * ceiling the tracked radius respects.
 */
const useTrackedFloorRadius = (
  meshRef: { current: Mesh | null },
  textures: { setRadius(radius: number): void } | null,
  fixedRadius: number | undefined,
  solid: number,
) => {
  const scene = useThree((state) => state.scene);
  // Deliberately not a `scale` prop on the mesh: R3F reapplies props on every
  // re-render, which would snap the patch back to whatever the last rendered
  // value was and undo the frame callback for a frame. The callback runs
  // before the renderer draws, so the first frame is already the right size.
  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh || !textures) return;
    // `STUDIO_FLOOR_MAX_RADIUS` binds a pinned radius too: past it the disc
    // would outrun the shadow layers under it.
    const radius =
      fixedRadius === undefined
        ? studioFloorRadiusForFootprint(
            readProductFootprintRadius(scene),
            solid,
          )
        : clampPinnedRadius(fixedRadius);
    // Unconditionally, and guarded inside `setRadius` against the radius it
    // last applied to the textures rather than against the mesh's scale: the
    // two do not always reset together. A change of `solid` remounts the
    // mesh — the component returns null for a commit while the fade map is
    // rebuilt — without re-acquiring the textures, so the mesh comes back at
    // scale 1 while the textures still hold the pre-remount repeat. Reading
    // the scale as a proxy for the texture state would then skip the
    // correction for any radius that happens to equal the unit circle's own.
    textures.setRadius(radius);
    // The circle is built in its own xy plane and laid flat by the mesh's
    // rotation, so those are the two axes that carry the radius — and both are
    // compared, not just x: a one-axis guard is the same "scale as a proxy for
    // state" hole the paragraph above rejects for the textures, and it would
    // leave the disc elliptical for as long as the radius did not change.
    if (mesh.scale.x !== radius || mesh.scale.y !== radius) {
      mesh.scale.set(radius, radius, 1);
    }
  });
};

/**
 * A patch of floor under the product, fading out towards its rim.
 *
 * Until this, nothing in the frame but the product responded to the studio:
 * the floor was the page background with a grid drawn on it, so the contact
 * pool and the cast shadow were grey decals on a white void, and the case
 * never looked like it stood anywhere. This is a lit surface. It takes its
 * brightness from the same environment cube the case does, so it darkens
 * with a darker studio and turns with the camera, the softboxes lay a faint
 * sheen across it, and the shadows fall on something.
 *
 * The rim fades to nothing rather than ending, so the grid and the page
 * background take over without an edge, whatever the theme.
 *
 * It does not write depth: the grid is composited over the patch while
 * still being depth-tested against the product. GTAO renders its G-buffer
 * through `scene.overrideMaterial`, so this disc is the ground the product
 * occludes against in that pass. Transparent, and skipped by
 * `StudioShadow`'s caster marking.
 */
export const StudioFloor = ({
  y,
  radius,
  solid = STUDIO_FLOOR_SOLID,
}: Readonly<StudioFloorProps>) => {
  const assets = useProductViewerAssets();
  const cached = useTexture(assets.floor);
  // Seeded at the unit circle's own radius, which is what the mesh is built
  // at. `useTrackedFloorRadius` asserts the real radius from `useFrame` before
  // the first draw, so in this canvas the seed is only ever the state the
  // variants hold in between; it is the hook's contract for a consumer that
  // renders without the frame loop.
  const textures = useOwnedStudioFloorTextures(cached, 1);
  const meshRef = useRef<Mesh>(null);
  useTrackedFloorRadius(meshRef, textures, radius, solid);

  const [fade, setFade] = useState<{
    texture: CanvasTexture;
    solid: number;
    disposed: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    const acquired = { texture: createFadeMap(solid), solid, disposed: false };
    setFade(acquired);
    return () => {
      acquired.disposed = true;
      acquired.texture.dispose();
    };
  }, [solid]);

  // Every map but the colour map is assigned on the material instead of being
  // a JSX prop: @react-three/fiber v8 tags each 8-bit RGBA texture handed over
  // as a prop with the renderer's output colour space (sRGB), whatever the
  // slot, which would decode the normal, ARM and fade data as colour. v9 does
  // so for colour maps only. Assigned here, they keep the colour space they
  // were configured with under either version. No dependency list: the
  // material is a new instance whenever the mesh remounts (a changed `solid`
  // returns null for a commit), and the maps can be the same objects then.
  const materialRef = useRef<MeshStandardMaterial>(null);
  useLayoutEffect(() => {
    const material = materialRef.current;
    if (!material || !textures || !fade) return;
    const { normalMap, arm } = textures;
    // The shader defines follow which maps are present, not which objects.
    const complete =
      material.normalMap !== null &&
      material.aoMap !== null &&
      material.roughnessMap !== null &&
      material.metalnessMap !== null &&
      material.alphaMap !== null;
    material.normalMap = normalMap;
    material.aoMap = arm;
    material.roughnessMap = arm;
    material.metalnessMap = arm;
    material.alphaMap = fade.texture;
    if (!complete) material.needsUpdate = true;
  });

  // Layout effects acquire resources and synchronously re-render before the
  // next frame. A suspended/abandoned render creates no owned GPU resources,
  // and StrictMode's cleanup/setup gets fresh variants, not disposed ones.
  if (!textures || !fade || fade.disposed || fade.solid !== solid) return null;
  const { map } = textures;

  return (
    <mesh
      ref={meshRef}
      position={[0, y, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      renderOrder={1}
    >
      {/* Unit circle: the radius is the mesh's scale, so changing it costs a
          matrix rather than a new BufferGeometry on every frame of a drag. */}
      <circleGeometry args={[1, 96]} />
      <meshStandardMaterial
        ref={materialRef}
        color={FLOOR_ALBEDO_LIFT}
        map={map}
        aoMapIntensity={FLOOR_AO_INTENSITY}
        roughness={1}
        metalness={1}
        transparent
        depthWrite={false}
      />
    </mesh>
  );
};
